import HistoryIcon from '@mui/icons-material/History';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createJobPresetVersion, getJobPresetVersions } from 'api/jobPreset';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { JobPreset } from 'interfaces/jobPreset';
import { useSnackbar } from 'notistack';
import React from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';

interface PresetVersionHistoryDialogProps {
  open: boolean;
  preset: JobPreset;
  onClose: () => void;
  onLoadVersion: (preset: JobPreset) => void;
  onVersionCreated: (preset: JobPreset) => void;
}

export const PresetVersionHistoryDialog = ({
  open,
  preset,
  onClose,
  onLoadVersion,
  onVersionCreated,
}: PresetVersionHistoryDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const versionsQuery = useQuery({
    queryKey: ['jobPresetVersions', preset.presetId],
    queryFn: ({ signal }) => getJobPresetVersions(preset.id, signal),
    enabled: open,
  });
  const restoreMutation = useMutation({
    mutationFn: (version: JobPreset) => createJobPresetVersion({ id: version.id }),
    onSuccess: (newVersion) => {
      queryClient.invalidateQueries({ queryKey: ['jobPresets'] });
      queryClient.invalidateQueries({ queryKey: ['jobPresetVersions', newVersion.presetId] });
      onVersionCreated(newVersion);
      enqueueSnackbar(`Preset version ${newVersion.versionNumber} restored`, { variant: 'success' });
      onClose();
    },
    onError: () => enqueueSnackbar('Error occurred, restore preset version failed', { variant: 'error' }),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="preset-version-history-title">
      <DialogChromeHeader
        title="Preset version history"
        subtitle={preset.name}
        titleId="preset-version-history-title"
        onClose={onClose}
      />
      <DialogContent dividers>
        {versionsQuery.isLoading ? (
          <CircularProgress aria-label="Loading preset versions" />
        ) : versionsQuery.isError ? (
          <Typography color="error">Preset version history could not be loaded.</Typography>
        ) : (
          <Table size="small" aria-label="Preset versions">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versionsQuery.data?.map((version) => {
                const isActive = !version.deletedAt;
                return (
                  <TableRow key={version.id} selected={version.id === preset.id}>
                    <TableCell>{version.versionNumber}</TableCell>
                    <TableCell>{formatDateTimeForUser(version.createdAt)}</TableCell>
                    <TableCell>{version.createdBy}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={isActive ? 'Current' : 'Archived'}
                        color={isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => {
                          onLoadVersion(version);
                          onClose();
                        }}
                      >
                        Load
                      </Button>
                      {!isActive && (
                        <Button
                          size="small"
                          startIcon={<HistoryIcon />}
                          disabled={restoreMutation.isLoading}
                          onClick={() => restoreMutation.mutate(version)}
                        >
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogChromeFooter>
        <Button onClick={onClose}>Close</Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
